import { useTheme } from '../../contexts/ThemeContext.tsx';
import AttendanceBadge from './AttendanceBadge.tsx';
import { useContactAttendanceSummary } from './useContactAttendanceSummary.ts';
/**
 * ChatHeader — topo da conversa selecionada.
 * Mostra avatar, nome, badges de status (Oficial / Janela 24h / Humano)
 * e o botão Painel. Gestão de tags ficou no painel lateral (ContactTagsPanel).
 */

import React from 'react';
import {
  ChevronLeft,
  CheckCircle2,
  PanelRightOpen,
  Timer,
} from 'lucide-react';
import { ConversationWindowStatus } from '../../services/api.ts';
import {
  ContactPresenceStatusLine,
  type ContactPresenceViewState,
} from './ContactPresence.tsx';
import ContactAvatar from '../ContactAvatar.tsx';
import ChannelBadge from './ChannelBadge.tsx';

interface ChatHeaderContact {
  id?: number;
  name?: string;
  phone: string;
  photo?: string;
  active_channel?: 'waha' | 'official_whatsapp' | 'instagram' | null;
  instagram_username?: string | null;
  human_mode?: boolean;
  pending_tasks_count?: number;
  next_task?: {
    priority?: 'low' | 'medium' | 'high' | 'urgent';
  };
}

interface ChatHeaderProps {
  attendanceEnabled?: boolean;
  contact: ChatHeaderContact;
  windowStatus: ConversationWindowStatus | null;
  contactFlowStatus: Record<string, any>;
  presence: ContactPresenceViewState;
  onBackToList: () => void;
  onShowTemplateSendModal: () => void;
  onOpenPanel: () => void;
  attendanceAction?: React.ReactNode;
}

const ChatHeader: React.FC<ChatHeaderProps> = ({
  contact,
  attendanceEnabled = false,
  windowStatus,
  contactFlowStatus,
  presence,
  onBackToList,
  onShowTemplateSendModal,
  onOpenPanel,
  attendanceAction,
}) => {
  const { isDark } = useTheme();
  const { ref, summary } = useContactAttendanceSummary(contact.id || 0, attendanceEnabled);
  const tasks = contact.pending_tasks_count || 0;
  const flowPaused = contact.active_channel !== 'instagram' && Object.values(contactFlowStatus).some((f: any) => f?.is_paused);

  const renderPanelBadge = () => {
    if (tasks > 0) {
      const priority = contact.next_task?.priority;
      const bgClass =
        priority === 'urgent' ? 'bg-red-500' :
        priority === 'high' ? 'bg-orange-500' :
        priority === 'medium' ? 'bg-yellow-500' :
        'bg-sminy-primary';

      return (
        <span className={`absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full text-white text-[10px] font-bold flex items-center justify-center shadow-sm ring-2 ring-white ${bgClass}`}>
          {tasks > 9 ? '9+' : tasks}
        </span>
      );
    }
    if (flowPaused) {
      return <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-white" />;
    }
    return null;
  };

  return (
    <div ref={ref} className={`flex-shrink-0 border-b ${isDark ? 'border-gray-700 bg-gray-900/95' : 'border-gray-200 bg-white/95'} backdrop-blur-sm sticky top-0 z-10 shadow-sm p-3 lg:px-6 lg:py-3`}>
      <div className="flex items-center justify-between">
        {/* Voltar (mobile) */}
        <button
          onClick={onBackToList}
          className={`lg:hidden p-2 -ml-2 rounded-lg ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-100'} transition-colors mr-2`}
          aria-label="Voltar para lista de contatos"
        >
          <ChevronLeft className={`w-6 h-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`} />
        </button>

        {/* Lado esquerdo: avatar + info */}
        <div className="flex items-center space-x-4 min-w-0 flex-1 lg:flex-initial">
          {/* Avatar */}
          <div className="relative">
            <ContactAvatar photo={contact.photo} name={contact.name || contact.phone} className="w-10 h-10 lg:w-12 lg:h-12 rounded-full" iconClassName="w-5 h-5 lg:w-6 lg:h-6" />
            <ChannelBadge channel={contact.active_channel} />
          </div>

          {/* Info: nome, badges, telefone, tags */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className={`text-base lg:text-lg font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'} truncate flex items-center gap-1.5`}>
                {contact.name}

                {/* Badge "Oficial" */}
                {contact.active_channel === 'official_whatsapp' && (
                  <div className="flex items-center gap-1 bg-green-50 px-2 py-0.5 rounded-full border border-green-100">
                    <CheckCircle2 className="w-4 h-4 text-green-500 fill-green-100" />
                    <span className="text-[10px] uppercase font-bold text-green-700 tracking-wide">Oficial</span>
                  </div>
                )}

                {/* Janela 24h (ativo / expirado) */}
                {contact.active_channel === 'official_whatsapp' && windowStatus && (
                  windowStatus.is_active ? (
                    <div
                      className="flex items-center gap-1 bg-sminy-primary/10 px-2 py-0.5 rounded-full border border-sminy-primary/20"
                      title={`Expira em ${windowStatus.time_remaining}`}
                    >
                      <Timer className="w-4 h-4 text-sminy-primary" />
                      <span className="text-[10px] font-bold text-sminy-primary">{windowStatus.time_remaining}</span>
                    </div>
                  ) : (
                    <button
                      onClick={onShowTemplateSendModal}
                      className="flex items-center gap-1 bg-amber-50 hover:bg-amber-100 px-2 py-0.5 rounded-full border border-amber-100 transition-colors cursor-pointer"
                      title="Janela de 24h expirada — clique para enviar template"
                    >
                      <Timer className="w-4 h-4 text-amber-500" />
                      <span className="text-[10px] font-bold text-amber-700">Enviar Template</span>
                    </button>
                  )
                )}
              </h2>

              <AttendanceBadge humanMode={contact.human_mode === true} summary={summary} />
            </div>

            {contact.active_channel === 'instagram' ? (
              <span className="text-xs text-gray-500">{contact.instagram_username ? `@${contact.instagram_username.replace(/^@/, '')}` : 'Instagram'}</span>
            ) : <ContactPresenceStatusLine
              phone={contact.phone}
              availability={presence.availability}
              lastSeen={presence.lastSeen}
              className={presence.availability || presence.lastSeen ? '' : 'font-mono tracking-tight'}
            />}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
        {/* Botão Painel */}
        <button
          onClick={onOpenPanel}
          className={`h-11 lg:h-10 px-3 py-2 ${isDark ? 'text-gray-300 bg-gray-800 border-gray-700' : 'text-gray-600 bg-gray-50/80 border-gray-200'} border hover:border-sminy-primary/30 hover:bg-sminy-primary/5 hover:text-sminy-primary hover:shadow-sm rounded-xl transition-all duration-200 relative flex items-center gap-2`}
          title="Abrir painel do contato (histórico, tarefas, anotações, fluxos)"
        >
          <PanelRightOpen className="h-5 w-5" />
          <span className="text-sm font-medium hidden lg:inline">Painel</span>
          {renderPanelBadge()}
        </button>
        {attendanceAction}
        </div>
      </div>
    </div>
  );
};

export default ChatHeader;
